/**
 * Which layer kinds may pull the Giro3D Instance into the converter's
 * `displayCrs` render frame (V-CRS-A).
 *
 * Only true scene geometry can: 3D Tiles / BIM / point clouds carry their own
 * coordinates and are aligned into whatever frame the Instance runs in.
 *
 * Raster and terrain layers cannot. Their whole pipeline is Web-Mercator-bound
 * — TiTiler serves `WebMercatorQuad` tiles, the Giro3D `Map` and the ColorLayer
 * source extents are built in EPSG:3857, and the Map entity is only created for
 * a Mercator frame. Letting a DEM/DTM COG switch the frame to its UTM
 * `displayCrs` left the scene with no `Map` to attach the ColorLayer to, so the
 * layer reported "ready" while the viewport stayed black.
 */

/** Layer kinds whose geometry can be rendered in a non-Mercator frame. */
const FRAME_SWITCHING_KINDS: ReadonlySet<string> = new Set([
  '3dtiles',
  'bim',
  'pointcloud',
]);

export function kindCanSwitchRenderFrame(kind: string | null | undefined): boolean {
  return FRAME_SWITCHING_KINDS.has((kind ?? '').toLowerCase());
}

/**
 * Should `addLayer` rebuild the Instance in this layer's `displayCrs`?
 *
 * True only when the layer's kind supports a non-Mercator frame, a
 * `displayCrs` is present, and it differs from the live frame.
 */
export function shouldSwitchRenderFrameForLayer(input: {
  readonly kind: string | null | undefined;
  readonly displayCrs?: string | null;
  readonly activeFrameCrs: string;
}): boolean {
  if (!kindCanSwitchRenderFrame(input.kind)) return false;
  const target = (input.displayCrs ?? '').trim();
  if (target.length === 0) return false;
  return target.toUpperCase() !== (input.activeFrameCrs ?? '').toUpperCase();
}
